import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Lightbulb, Lock, Loader2 } from 'lucide-react';
import * as api from '../../lib/api';
import { prepareImage } from '../../lib/imageResize';
import { THEMES, SLIDES, defaultValues, suggestEmail } from './slides';
import SlideView from './SlideView';
import CvView from './CvView';
import { buildFiles, saveBlob } from './exporter';
import './wifey.css';

const STORE = 'wifey_builder_v1';      // her draft, on this device only
const UNLOCKED = 'wifey_unlocked';     // the confirmed email on this device
const PENDING = 'wifey_pending';       // the email we are waiting to hear back on
// Public Tally template (screener) that sisters duplicate into their own free Tally account for brothers to fill out
const FORM_TEMPLATE_URL = 'https://tally.so/templates/wifey-application/n09P0m';
const POLL_MS = 12000;
const POLL_STOP_MS = 15 * 60 * 1000;

const read = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };
const drop = (key) => { try { localStorage.removeItem(key); } catch { /* storage unavailable: the page still works */ } };
const toDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('That photo could not be read.'));
  r.readAsDataURL(file);
});

const Crescent = ({ id = 'wbg' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <defs><linearGradient id={id} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4AADA8" /><stop offset="1" stopColor="#A99CC8" /></linearGradient></defs>
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" fill={`url(#${id})`} />
  </svg>
);

const initial = () => {
  const saved = read(STORE) || {};
  const ids = SLIDES.map((s) => s.id);
  const on = Array.isArray(saved.on) ? saved.on.filter((i) => ids.includes(i)) : ids;
  return {
    theme: THEMES.some((t) => t.id === saved.theme) ? saved.theme : 'autumn',
    on: on.length ? on : ids,
    vals: { ...defaultValues(), ...(saved.vals || {}) },
    photos: saved.photos && typeof saved.photos === 'object' ? saved.photos : {},
  };
};

const WifeyBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState(initial);
  const { theme, on, vals, photos } = draft;
  const [cur, setCur] = useState(0);
  const [view, setView] = useState('edit');
  const [open, setOpen] = useState(() => new Set());
  const [photoError, setPhotoError] = useState('');
  const [scale, setScale] = useState(0.4);

  const [gate, setGate] = useState('closed'); // closed | ask | sent | done
  const [unlocked, setUnlocked] = useState(() => read(UNLOCKED) || '');
  const [email, setEmail] = useState(() => read(PENDING) || '');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [waitNote, setWaitNote] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [building, setBuilding] = useState(false);
  const [buildId, setBuildId] = useState(0);
  const [files, setFiles] = useState(null);
  const [buildError, setBuildError] = useState('');

  const dialogRef = useRef(null);
  const stageRef = useRef(null);
  const exportRef = useRef(null);

  const included = useMemo(() => SLIDES.filter((s) => on.includes(s.id)), [on]);
  const activeSlide = included[Math.min(cur, included.length - 1)];

  useEffect(() => {
    const previous = document.title;
    document.title = 'The Wifey Application Builder | Sirati';
    return () => { document.title = previous; };
  }, []);

  // Keep her draft on this device (photos too, if they fit)
  useEffect(() => {
    const t = setTimeout(() => {
      if (!write(STORE, draft)) write(STORE, { ...draft, photos: {} });
    }, 400);
    return () => clearTimeout(t);
  }, [draft]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => setScale(el.clientWidth / 1080);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [view]);

  useEffect(() => { if (cur > included.length - 1) setCur(Math.max(0, included.length - 1)); }, [included.length, cur]);

  const setVal = (key, value) => setDraft((d) => ({ ...d, vals: { ...d.vals, [key]: value } }));
  const toggleSlide = (id) => setDraft((d) => {
    if (d.on.includes(id)) return d.on.length > 1 ? { ...d, on: d.on.filter((i) => i !== id) } : d;
    return { ...d, on: SLIDES.map((s) => s.id).filter((i) => i === id || d.on.includes(i)) };
  });
  const focusSlide = (id) => { const i = included.findIndex((s) => s.id === id); if (i >= 0) setCur(i); };

  const addPhoto = async (id, file) => {
    setPhotoError('');
    try {
      const prepared = await prepareImage(file, { maxDimension: 1600 });
      const url = await toDataUrl(prepared.file);
      setDraft((d) => ({ ...d, photos: { ...d.photos, [id]: url } }));
    } catch (err) {
      setPhotoError(err?.message || 'That photo could not be added.');
    }
  };
  const removePhoto = (id) => setDraft((d) => { const p = { ...d.photos }; delete p[id]; return { ...d, photos: p }; });

  /* ───────────── files ───────────── */
  const startBuild = useCallback(() => {
    window.getSelection()?.removeAllRanges();   // a stray text highlight must never end up in the slides
    setFiles(null);
    setBuildError('');
    setBuilding(true);
    setBuildId((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!building) return undefined;
    let cancelled = false;
    (async () => {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      try {
        const root = exportRef.current;
        const slideNodes = [...root.querySelectorAll('[data-slide]')].map((n) => n.firstElementChild);
        const labels = [...root.querySelectorAll('[data-slide]')].map((n) => n.dataset.label);
        const cvNode = root.querySelector('[data-cv]').firstElementChild;
        const built = await buildFiles({ slideNodes, cvNode, labels });
        if (cancelled) return;
        setFiles(built);
        setBuilding(false);
        saveBlob(built.zip, 'wifey-application-slides.zip');
        setTimeout(() => saveBlob(built.pdf, 'wifey-application-cv.pdf'), 700);
      } catch (err) {
        if (cancelled) return;
        setBuildError('We could not make your files. Please try again.');
        setBuilding(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildId]);

  /* ───────────── email step ───────────── */
  const unlock = useCallback((address) => {
    const clean = String(address || '').trim().toLowerCase();
    write(UNLOCKED, clean);
    drop(PENDING);
    setUnlocked(clean);
    setWaitNote('');
    setGate('done');
    startBuild();
  }, [startBuild]);

  const check = useCallback(async (manual = false) => {
    const address = (email || read(PENDING) || '').trim();
    if (!address) return;
    try {
      const res = await api.wifeyStatus(address);
      if (res.data?.confirmed) { unlock(address); return; }
      if (manual) setWaitNote('Not yet. Please tap the link in the email we sent. If you cannot find it, check your spam folder.');
    } catch {
      if (manual) setWaitNote('We could not check just now. Please try again in a moment.');
    }
  }, [email, unlock]);

  // Back from the confirmation link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('confirmed') !== '1') return;
    navigate(location.pathname, { replace: true });
    const pending = read(PENDING);
    if (pending) { setEmail(pending); setGate('sent'); } else { setJustConfirmed(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While waiting: check every few seconds, and the moment the tab is looked at again
  useEffect(() => {
    if (gate !== 'sent') return undefined;
    setTimedOut(false);
    check();
    const poll = setInterval(check, POLL_MS);
    const stop = setTimeout(() => { clearInterval(poll); setTimedOut(true); }, POLL_STOP_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(poll);
      clearTimeout(stop);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [gate, check]);

  const submit = async (e, force = false) => {
    if (e) e.preventDefault();
    const address = email.trim();
    if (!force) {
      const fix = suggestEmail(address);
      if (fix) { setSuggestion(fix); return; }
    }
    setSuggestion('');
    setBusy(true);
    setFormError('');
    try {
      const res = await api.wifeyRequest(address);
      write(PENDING, address.toLowerCase());
      if (res.data?.status === 'confirmed') unlock(address);
      else { setWaitNote(''); setGate('sent'); }
    } catch (err) {
      setFormError(err?.response?.data?.detail || 'Something went wrong. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const onDownload = () => {
    if (!included.length) return;
    if (unlocked) { setGate('done'); startBuild(); } else { setFormError(''); setSuggestion(''); setGate('ask'); }
  };

  // Native dialog: focus is trapped and Escape closes it
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (gate !== 'closed' && !d.open) d.showModal();
    if (gate === 'closed' && d.open) d.close();
  }, [gate]);

  const closeGate = () => setGate('closed');
  const year = new Date().getFullYear();

  return (
    <div className="wb ph-no-capture" data-view={view} lang="en">
      <div className="bar">
        <span />
        <a className="brand" href="/" aria-label="Sirati home"><Crescent id="wbg-bar" />Sirati</a>
      </div>

      <header className="top">
        <div>
          <span className="eyebrow"><i />A free tool for sisters</span>
          <h1>The Wifey Application <em>Builder</em></h1>
          <p className="lede">Put your story, your values and what you are looking for into a few clean slides, ready to share. Pick a theme, answer a few short prompts, and download your carousel and a one-page PDF.</p>
          <p className="aboutus"><Crescent id="wbg-about" /><span><b>Sirati</b> is a new social network for Muslim singles, here to support you on your journey to finding a spouse. <em>Created <i>by</i> a single Muslim, for single Muslims.</em></span></p>
        </div>
        <p className="steps-note"><b>Everything stays on your device.</b> Your words and photos are never uploaded while you build. You only enter your email when you are ready to download.</p>
      </header>

      {justConfirmed && (
        <div className="banner" role="status">
          <p>Your email is confirmed. If you started your slides on another device or tab, go back there and your downloads will unlock. Or build your application right here.</p>
        </div>
      )}

      <div className="seg" role="tablist" aria-label="Write or preview">
        <button type="button" role="tab" aria-selected={view === 'edit'} onClick={() => setView('edit')}>Write</button>
        <button type="button" role="tab" aria-selected={view === 'preview'} onClick={() => setView('preview')}>Preview</button>
      </div>

      <main>
        <section className="editor" aria-label="Build your application">
          <div className="block">
            <h2><span>01</span>Choose a theme</h2>
            <p className="hint">You can switch any time. Your words carry over.</p>
            <div className="themes" role="radiogroup" aria-label="Theme">
              {THEMES.map((t) => (
                <button key={t.id} type="button" className="theme" role="radio" aria-checked={theme === t.id}
                  onClick={() => setDraft((d) => ({ ...d, theme: t.id }))}>
                  <span className={`swatch ${t.swatch}`}><i /><small>{t.kicker}</small><b>About me</b></span>
                  <span className="nm">{t.name}</span><span className="ds">{t.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="block">
            <h2><span>02</span>Choose your slides</h2>
            <p className="hint">Use the ones that feel right and leave out the rest. None are required.</p>
            <div className="pick">
              {SLIDES.map((s) => (
                <button key={s.id} type="button" className="chip" aria-pressed={on.includes(s.id)} onClick={() => toggleSlide(s.id)}>
                  {on.includes(s.id) && <Check aria-hidden="true" />}{s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="block">
            <h2><span>03</span>Write it your way</h2>
            <p className="hint">Short and warm works best. Open a slide to edit it, and watch the preview change.</p>
            {included.map((s, idx) => {
              const isOpen = open.has(s.id) || (open.size === 0 && idx === 0);
              return (
                <div key={s.id} className={`panel ${isOpen ? 'open' : ''}`}>
                  <button type="button" aria-expanded={isOpen} aria-controls={`wb-p-${s.id}`}
                    onClick={() => { setOpen((o) => { const n = new Set(o.size ? o : [included[0].id]); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; }); focusSlide(s.id); }}>
                    <span>{s.label}</span><ChevronDown className="chev" aria-hidden="true" />
                  </button>
                  <div className="body" id={`wb-p-${s.id}`}>
                    <div className="tip"><Lightbulb aria-hidden="true" /><span>{s.tip}</span></div>
                    {s.fields.map((f) => {
                      const key = `${s.id}.${f.k}`;
                      const id = `wb-f-${s.id}-${f.k}`;
                      if (f.type === 'check') {
                        return (
                          <label key={key} className="check" htmlFor={id}>
                            <input id={id} type="checkbox" checked={!!vals[key]} onChange={(e) => { setVal(key, e.target.checked); focusSlide(s.id); }} />
                            <span>{f.label}</span>
                          </label>
                        );
                      }
                      const props = { id, value: vals[key] || '', maxLength: f.max, onChange: (e) => { setVal(key, e.target.value); focusSlide(s.id); }, onFocus: () => focusSlide(s.id) };
                      return (
                        <div className="field" key={key}>
                          <label htmlFor={id}>{f.label}</label>
                          {f.type === 'area' ? <textarea {...props} /> : <input type="text" {...props} />}
                          <span className="count" aria-hidden="true">{(vals[key] || '').length}/{f.max}</span>
                        </div>
                      );
                    })}
                    {s.photo && (
                      <div className="photo">
                        <span className="thumb" style={photos[s.id] ? { backgroundImage: `url("${photos[s.id]}")` } : undefined} aria-hidden="true" />
                        <label className="btn small" style={{ cursor: 'pointer' }}>
                          {photos[s.id] ? (s.photo === 'bg' ? 'Change background image' : 'Change photo') : (s.photo === 'bg' ? 'Add a background image (optional)' : 'Add a photo (optional)')}
                          <input type="file" accept="image/*" className="sr" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) addPhoto(s.id, f); }} />
                        </label>
                        {photos[s.id] && <button type="button" className="btn small" onClick={() => removePhoto(s.id)}>Remove</button>}
                      </div>
                    )}
                    {photoError && open.has(s.id) && <p className="err" role="alert">{photoError}</p>}
                  </div>
                </div>
              );
            })}
            <p className="privacy"><Lock aria-hidden="true" />Photos and text stay in your browser. Nothing is sent to Sirati until you ask for your download.</p>
            <div className="editor-cta">
              <button type="button" className="btn primary" onClick={() => { onDownload(); }}>Download my slides and PDF</button>
            </div>
          </div>
        </section>

        <aside className="pv" aria-label="Preview">
          <div className="stage" ref={stageRef} aria-hidden="true">
            {included.map((s, i) => (
              <div key={s.id} className={`slide-wrap ${i === cur ? 'on' : ''}`}>
                <div className="scaled" style={{ transform: `scale(${scale})` }}>
                  <SlideView theme={theme} slideId={s.id} index={i} vals={vals} photo={photos[s.id]} />
                </div>
              </div>
            ))}
          </div>
          <div className="pvbar">
            <button type="button" className="arrow" aria-label="Previous slide" onClick={() => setCur((c) => (c - 1 + included.length) % included.length)}><ChevronLeft aria-hidden="true" /></button>
            <div className="dots" role="group" aria-label="Choose slide">
              {included.map((s, i) => (
                <button key={s.id} type="button" className="dot" aria-label={`Slide ${i + 1}: ${s.label}`} aria-current={i === cur ? 'true' : undefined} onClick={() => setCur(i)}><i /></button>
              ))}
            </div>
            <button type="button" className="arrow" aria-label="Next slide" onClick={() => setCur((c) => (c + 1) % included.length)}><ChevronRight aria-hidden="true" /></button>
          </div>
          <p className="status" aria-live="polite">{activeSlide ? `Slide ${Math.min(cur, included.length - 1) + 1} of ${included.length}: ${activeSlide.label}` : ''}</p>
          <div className="export">
            <button type="button" className="btn primary" onClick={onDownload}>Download my slides and PDF</button>
          </div>
        </aside>
      </main>

      <footer className="foot">
        <span>&copy; {year} Sirati &middot; <a href="https://sirati.co">sirati.co</a></span>
        <span>Inspired by our good sis <a href="https://www.instagram.com/nailahdean28/" target="_blank" rel="noopener noreferrer">NailahDean</a></span>
      </footer>

      <dialog ref={dialogRef} aria-labelledby="wb-gate-title" onClose={() => setGate('closed')}>
        <div className="gate">
          {gate === 'ask' && (
            <form onSubmit={submit} noValidate>
              <h3 id="wb-gate-title">Where should we send your slides?</h3>
              <p>Your slides are ready. Enter your email and we will send a quick confirmation. Tap the link in your email to unlock your downloads.</p>
              <div className="field">
                <label htmlFor="wb-email">Email</label>
                <input id="wb-email" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" value={email}
                  onChange={(e) => { setEmail(e.target.value); setSuggestion(''); setFormError(''); }} aria-invalid={!!formError} aria-describedby={formError ? 'wb-email-err' : undefined} />
              </div>
              {suggestion && (
                <div className="suggest" role="status">
                  Did you mean <b>{suggestion}</b>?
                  <div className="row">
                    <button type="button" className="btn small" onClick={() => { setEmail(suggestion); setSuggestion(''); }}>Yes, use that</button>
                    <button type="button" className="btn small" onClick={() => submit(null, true)}>No, keep mine</button>
                  </div>
                </div>
              )}
              {formError && <p className="err" id="wb-email-err" role="alert">{formError}</p>}
              <div className="row">
                <button type="submit" className="btn primary" disabled={busy}>{busy ? 'One moment...' : 'Join the Sirati email list and download'}</button>
                <button type="button" className="btn" onClick={closeGate}>Go back</button>
              </div>
              <p className="fine">By joining, you will get marriage-readiness tips, invites to in-person and online events for Muslim Singles and so much more! Unsubscribe any time. We never post anything for you, and your slides are not stored on our servers.</p>
            </form>
          )}

          {gate === 'sent' && (
            <div>
              <h3 id="wb-gate-title">Check your inbox</h3>
              <p>We sent a confirmation to <b>{email}</b>. Tap the link in your email to unlock your downloads. If you do not see it in a minute, check your spam folder.</p>
              <p className="fine" aria-live="polite">{timedOut ? 'Still waiting? You can use a different email, or go back and try again later.' : 'This screen unlocks by itself once you confirm.'}</p>
              {waitNote && <p className="err" role="status">{waitNote}</p>}
              <div className="row">
                <button type="button" className="btn primary" onClick={() => check(true)}>I have confirmed, unlock my downloads</button>
              </div>
              <div className="row">
                <button type="button" className="btn small" onClick={() => { setFormError(''); setGate('ask'); }}>Use a different email</button>
                <button type="button" className="btn small" onClick={closeGate}>Go back and keep editing</button>
              </div>
            </div>
          )}

          {gate === 'done' && (
            <div>
              {building && (
                <>
                  <h3 id="wb-gate-title">Getting your files ready</h3>
                  <p className="building" role="status"><Loader2 className="spin" aria-hidden="true" />This takes a few seconds.</p>
                </>
              )}
              {!building && buildError && (
                <>
                  <h3 id="wb-gate-title">Something went wrong</h3>
                  <p className="err" role="alert">{buildError}</p>
                  <div className="row"><button type="button" className="btn primary" onClick={startBuild}>Try again</button></div>
                </>
              )}
              {!building && files && (
                <>
                  <h3 id="wb-gate-title">Your files are downloading</h3>
                  <p>Your slides and your one-page PDF are on their way. If nothing starts, use the buttons below.</p>
                  <div className="row">
                    <button type="button" className="btn primary" onClick={() => saveBlob(files.zip, 'wifey-application-slides.zip')}>Download all slides (ZIP)</button>
                    <button type="button" className="btn" onClick={() => saveBlob(files.pdf, 'wifey-application-cv.pdf')}>Download PDF (one-page CV)</button>
                  </div>
                  <div className="chips-row" aria-label="Download one slide">
                    {files.slides.map((f, i) => (
                      <button key={f.name} type="button" className="btn" onClick={() => saveBlob(f.blob, f.name)}>Slide {i + 1}</button>
                    ))}
                  </div>
                  <div className="next">
                    <h4>Next: your application form for brothers</h4>
                    <p>Your last slide tells brothers where to apply. This ready-made screener form is yours to change. Open it and duplicate it into a free Tally account, then add, remove or reword any question. Brothers do not need an account to fill it in. Use a separate email for your account, and share the answers with your wali.</p>
                    <a className="btn" href={FORM_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">Get the form template<span className="sr"> (opens in a new tab)</span></a>
                  </div>
                  <div className="cta">
                    <Crescent id="wbg-cta" />
                    <div>
                      <p><b>The Wifey Application Builder is created by Sirati</b>, a new social network for single Muslims. Join us!</p>
                      <a className="btn primary wrap" href="/">Connect with other Muslim Singles.<br />Join Our Free Community</a>
                    </div>
                  </div>
                </>
              )}
              <div className="row"><button type="button" className="btn small" onClick={closeGate}>Close</button></div>
            </div>
          )}
        </div>
      </dialog>

      {building && (
        <div className="wb-export" ref={exportRef} aria-hidden="true">
          {included.map((s, i) => (
            <div key={s.id} data-slide data-label={s.label}>
              <SlideView theme={theme} slideId={s.id} index={i} vals={vals} photo={photos[s.id]} />
            </div>
          ))}
          <div data-cv><CvView theme={theme} included={included.map((s) => s.id)} vals={vals} photos={photos} /></div>
        </div>
      )}
    </div>
  );
};

export default WifeyBuilderPage;
